import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";

export const cardVariants = cva(
  cn("flex w-full flex-col rounded-md border text-left", REDUCED_MOTION_SAFE),
  {
    variants: {
      tone: {
        default: "border-hairline bg-surface",
        /**
         * AT MOST ONE tinted card per screen, reserved for the
         * single most important thing on it — this round's active pick, a
         * just-clinched win. Navigation chrome does not count against the
         * budget; a second tinted card on the same screen means neither one
         * reads as special anymore.
         */
        tinted: "border-primary bg-primary-tint",
        /** A card that carries a status. Still paired with a text label. */
        success: "border-success bg-success-tint",
        warning: "border-warning bg-warning-tint",
        error: "border-error bg-error-tint",
      },
      padding: {
        /** The default body padding. */
        default: "p-4",
        /** The compact body padding. There is no third value. */
        compact: "p-3",
        /** For a card whose children own their own padding (a list of rows). */
        none: "p-0",
      },
      elevation: {
        flat: "shadow-none",
        raised: "shadow-sm",
      },
      interactive: {
        true: cn(
          "cursor-pointer transition-colors duration-100 ease-standard",
          "hover:border-line-strong active:bg-surface-sunken",
          FOCUS_RING,
        ),
        false: "",
      },
    },
    defaultVariants: {
      tone: "default",
      padding: "default",
      elevation: "flat",
      interactive: false,
    },
  },
);

type typeCardOwnProps = Omit<
  VariantProps<typeof cardVariants>,
  "interactive"
> & {
  /** Section title. Sentence case — never an uppercase overline. */
  title?: string;
  /** A status badge or a count, rendered beside the title. */
  badge?: ReactNode;
  /** A section-level action, top-right. One at most. */
  action?: ReactNode;
  /** Pinned below the body, separated by a hairline. */
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
};

export type typeCardProps = typeCardOwnProps &
  Omit<ComponentPropsWithoutRef<"div">, "title" | "children"> & {
    /**
     * A card that is not the whole tap target. The default.
     *
     * `ui-patterns.md`: an item card in a list IS the tap target — pass
     * `onClick` and it renders as a real button, so it is reachable by keyboard
     * and announced as clickable, instead of a div with a click handler.
     */
    onClick?: never;
  };

export type typeInteractiveCardProps = typeCardOwnProps &
  Omit<ComponentPropsWithoutRef<"button">, "title" | "children"> & {
    onClick: () => void;
  };

/**
 * The platform's container. Everything a fan browses casually is a stack of
 * these, never a dense data table.
 *
 * Body padding has exactly two values, 16px and 12px, straight from the ladder.
 * A one-off inline padding on a card is the start of the ragged spacing that
 * reads as a bug to someone glancing at their phone.
 */
export const Card = ({
  title,
  badge,
  action,
  footer,
  children,
  tone,
  padding,
  elevation,
  className,
  ...rest
}: typeCardProps | typeInteractiveCardProps): ReactNode => {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const interactive =
    typeof (rest as { onClick?: unknown }).onClick === "function";

  const body = (
    <>
      {title || badge || action ? (
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {title ? (
              <h3 id={titleId} className="text-h2 text-ink">
                {title}
              </h3>
            ) : null}
            {badge}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </>
  );

  const classes = cn(
    cardVariants({ tone, padding, elevation, interactive }),
    (title || badge || action) && children ? "gap-3" : undefined,
    className,
  );

  if (interactive) {
    const { onClick, ...buttonRest } =
      rest as ComponentPropsWithoutRef<"button">;
    return (
      <button
        type="button"
        onClick={onClick}
        aria-labelledby={title ? titleId : undefined}
        className={classes}
        {...buttonRest}
      >
        {body}
        {footer ? (
          <div className="mt-3 border-t border-hairline pt-3">{footer}</div>
        ) : null}
      </button>
    );
  }

  return (
    <div className={classes} {...(rest as ComponentPropsWithoutRef<"div">)}>
      {body}
      {footer ? (
        <div className="mt-3 border-t border-hairline pt-3">{footer}</div>
      ) : null}
    </div>
  );
};
